import { useState, useEffect } from "react";

// OpenLayers
import OlMap from "ol/Map";

import { Popup, PopupProps } from "lib/classes/popup";
import { getLocaleString } from "lib/localization/vendor";
import { assetURL } from "lib/utilities/asset";
import youtube from "lib/utilities/youtube";

import parse from "html-react-parser";

// Models
import MapToolMode from "lib/enums/maptoolmode";

// Tools
import { getToolEnabled } from "lib/utilities/toolmodes";

import "styles/css/Guide.scss";

enum GuideTab {
	Introduction,
	SelectTool,
	LocationsTool,
	MapLayersTool,
	AddressSearchTool,
	ExportTool,
	PrintTool,
	ZoomTool,
	BaseMapTool,
	LanguageTool,
	Finish
};
var tabs:GuideTab[] = [];

const Guide = new Popup({
	title: () => getLocaleString("tutorial"),
	style: {width: "80%", maxWidth: 900},
	render: function() {
		const [currentTab, setCurrentTab] = useState<GuideTab>(GuideTab.Introduction);
		
		const map:OlMap = this.get("map");
		const mapConfig:any = map.get("opus-config");
		const tabIndex:number = tabs.indexOf(currentTab);
		
		// Add all the tabs
		useEffect(() => {
			tabs = []; // Reset array
			
			tabs.push(GuideTab.Introduction);
			
			if(getToolEnabled(MapToolMode.Select, mapConfig)) {
				tabs.push(GuideTab.SelectTool);
				tabs.push(GuideTab.ZoomTool);
			}
			
			if(getToolEnabled(MapToolMode.Locations, mapConfig))
				tabs.push(GuideTab.LocationsTool);
			
			if(getToolEnabled(MapToolMode.MapLayers, mapConfig))
				tabs.push(GuideTab.MapLayersTool);
			
			if(getToolEnabled(MapToolMode.Address, mapConfig))
				tabs.push(GuideTab.AddressSearchTool);
			
			if(getToolEnabled(MapToolMode.Print, mapConfig))
				tabs.push(GuideTab.PrintTool);
			
			if(getToolEnabled(MapToolMode.Print, mapConfig))
				tabs.push(GuideTab.ExportTool);
			
			tabs.push(GuideTab.BaseMapTool);
			
			if(mapConfig.locales?.length > 1)
				tabs.push(GuideTab.LanguageTool);
			
			tabs.push(GuideTab.Finish);
		}, []);
		
		const drawOptions = () => {
			const rows:any[] = [];
			
			tabs.forEach((tab:GuideTab) => {
				const tabName:string = String(GuideTab[tab]);
				
				let className:string = "unit-option";
				let circleClass:string = "num-circle";
				
				if(currentTab == tab)
					className += ` unit-option--selected ${_ui.getSelClassName()}`;
					
				if(tabs.indexOf(tab) < tabIndex)
					circleClass += " num-circle--done";
				
				rows.push(
					<div 
						className={className} 
						key={tabs.indexOf(tab)} 
						onClick={() => setCurrentTab(tab)}
					>
						<div className={circleClass}>
							<div className="num-circle--text">
								{tabs.indexOf(tab) + 1}
							</div>
						</div>
						{getLocaleString(tabName.toLowerCase())}
					</div>
				)
			});
			
			return rows;
		}
		const drawGuideTab = () => {
			switch(currentTab) {
				case GuideTab.Introduction: {
					return (
						<>
							<h3>{getLocaleString("tutorialtitle")}</h3>
							
							<p>{getLocaleString("tutorialsub")}</p>
							
							<div className="guide-start-buttons">
								<button onClick={() => nextGuideTab()}>
									{getLocaleString("starttutorial")}
								</button>
								<button onClick={() => skip()}>
									{getLocaleString("skiptutorial")}
								</button>
							</div>
						</>
					)
				}
				case GuideTab.SelectTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/select.png")} className="tool-icon" />
									{getLocaleString("selecttool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_select1")}</li>
									<li>{getLocaleString("guide_select2")}</li>
								</p>
							</div>
							<p>
								{youtube("YGYFFoYV_uI")}
							</p>
						</>
					)
				}
				case GuideTab.LocationsTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/locations.png")} className="tool-icon" />
									{getLocaleString("locationstool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_loc1")}</li>
								</p>
							</div>
							<p>
								{youtube("i947-MiennU")}
							</p>
						</>
					)
				}
				case GuideTab.MapLayersTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/maplayers.png")} className="tool-icon" />
									{getLocaleString("maplayerstool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_ml1")}</li>
									<li>{getLocaleString("guide_ml2")}</li>
								</p>
							</div>
							<p>
								{youtube("qZVw0SglOPc")}
							</p>
						</>
					)
				}
				case GuideTab.AddressSearchTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/address.png")} className="tool-icon" />
									{getLocaleString("addresssearchtool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_as1")}</li>
									<li>{getLocaleString("guide_as2")}</li>
								</p>
							</div>
							<p>
								{youtube("BhCaQpcXcMg")}
							</p>
						</>
					)
				}
				case GuideTab.ExportTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/pdf.png")} className="tool-icon" />
									{getLocaleString("exporttool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_et1")}</li>
									<li>{getLocaleString("guide_et2")}</li>
								</p>
							</div>
							<p>
								{youtube("Gs-Dcw685B0")}
							</p>
						</>
					)
				}
				case GuideTab.PrintTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/print.png")} className="tool-icon" />
									{getLocaleString("printtool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_pt1")}</li>
									<li>{getLocaleString("guide_pt2")}</li>
								</p>
							</div>
							<p>
								{youtube("mSC-fZXuvmI")}
							</p>
						</>
					)
				}
				case GuideTab.ZoomTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/zoom.png")} className="tool-icon" />
									{getLocaleString("zoomtool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_zt1")}</li>
								</p>
							</div>
							<p>
								{youtube("mSmLRCcxREs")}
							</p>
						</>
					)
				}
				case GuideTab.BaseMapTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/basemap.png")} className="tool-icon" />
									{getLocaleString("basemaptool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_bm1")}</li>
								</p>
							</div>
							<p>
								{youtube("md9B9LJ8OCg")}
							</p>
						</>
					)
				}
				case GuideTab.LanguageTool: {
					return (
						<>
							<div className="tab-heading">
								<h3>
									<img src={assetURL("assets/images/language.png")} className="tool-icon" />
									{getLocaleString("languagetool")}
								</h3>
								<p>
									<li>{getLocaleString("guide_lt1")}</li>
								</p>
							</div>
							<p>
								{youtube("Rjy14C_Armo")}
							</p>
						</>
					)
				}
				case GuideTab.Finish: {
					return (
						<>
							<div className="tab-heading">
								<h2>
									{getLocaleString("guide_finish1")}
								</h2>
								<p>{parse(getLocaleString("guide_finish2"))}</p>
							</div>
							<div style={{height: 160}}></div>
						</>
					)
				}
			}
		}
		const drawNextButton = () => {
			if(tabIndex > tabs.length-2 || tabIndex < 1)
				return null;
			
			return (
				<button onClick={() => nextGuideTab()} style={{width: 80}}>
					{getLocaleString("next")}
				</button>
			)
		}
		const drawBackButton = () => {
			if(tabIndex < 1)
				return null;
			
			return (
				<button onClick={() => prevGuideTab()} style={{width: 80}}>
					{getLocaleString("back")}
				</button>
			)
		}
		const drawFinishButton = () => {
			if(tabIndex < tabs.length-1)
				return null;
			
			return (
				<button onClick={finish} style={{width: 80}}>
					{getLocaleString("finish")}
				</button>
			)
		}
		const finish = () => {
			localStorage.setItem("omv-guide-shown", "true"); // Stop the tutorial showing automatically in the future
			_ui.closePopupOf(this);
		}
		const skip = () => {
			finish();
			//_ui.showNotice(getLocaleString("skiptutnotice"));
		}
		
		const nextGuideTab = () => {
			setCurrentTab(tabs[tabIndex+1]);
		}
		const prevGuideTab = () => {
			if(tabIndex == 0)
				return setCurrentTab(GuideTab.Introduction);
				
			setCurrentTab(tabs[tabIndex-1]);
		}
		
		const drawGuideList = () => {
			if(_dimensions.width < 800)
				return;
			
			return (
				<div className="guide-list">
					{drawOptions()}
				</div>
			)
		}
		
		return (
			<div className="guide-container">
				{drawGuideList()}
				<div className="guide-info">
					{drawGuideTab()}
					<div className="guide-info--buttons">
						{drawBackButton()}
						{drawNextButton()}
						{drawFinishButton()}
					</div>
				</div>
			</div>
		)
	}
});

export default Guide;